(function () {
  'use strict';

  function escapeHtml(value) {
    return String(value || '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  function decodeHtmlEntities(value) {
    if (typeof value !== 'string' || value.indexOf('&') === -1) return value;
    const textarea = document.createElement('textarea');
    textarea.innerHTML = value;
    return textarea.value;
  }

  function decodeStringMap(value) {
    if (!value || typeof value !== 'object') return value || {};
    return Object.entries(value).reduce(function decodeMap(output, entry) {
      output[entry[0]] = decodeHtmlEntities(entry[1]);
      return output;
    }, {});
  }

  function normalizeConsultations(value) {
    if (!Array.isArray(value)) return [];
    const seen = new Set();
    return value.filter(function keepItem(item) {
      if (!item || typeof item !== 'object') return false;
      const id = String(item.assessment_id || item.assessmentId || '').trim();
      const url = String(item.result_url || item.resultUrl || '').trim();
      if (!id || !url || seen.has(id)) return false;
      seen.add(id);
      return true;
    }).slice(0, 20);
  }

  function mergeConsultations(primary, secondary) {
    const byId = new Map();
    normalizeConsultations([...(primary || []), ...(secondary || [])]).forEach(function add(item) {
      byId.set(String(item.assessment_id || item.assessmentId), item);
    });
    return Array.from(byId.values()).sort(function byNewest(a, b) {
      return new Date(b.created_at || 0).getTime() - new Date(a.created_at || 0).getTime();
    }).slice(0, 20);
  }

  function formatDate(value, locale) {
    const parsed = new Date(value || '');
    if (Number.isNaN(parsed.getTime())) return '';
    try {
      return new Intl.DateTimeFormat(locale || undefined, {
        day: 'numeric',
        month: 'long',
        year: 'numeric'
      }).format(parsed);
    } catch (error) {
      return parsed.toLocaleDateString();
    }
  }

  function getStoreLabel(storeCode, labels) {
    const code = String(storeCode || '').toLowerCase();
    if (!code) return '';
    return labels[code] || code.toUpperCase();
  }

  function shouldShowLanguageBadge(language, locale) {
    const lang = String(language || '').toLowerCase().split('-')[0];
    const current = String(locale || '').toLowerCase().split('-')[0];
    return Boolean(lang && current && lang !== current);
  }

  function renderCards(section, consultations, config) {
    const grid = section.querySelector('[data-my-consultations-grid]');
    const empty = section.querySelector('[data-my-consultations-empty]');
    const loading = section.querySelector('[data-my-consultations-loading]');
    if (!grid) return;

    if (loading) loading.hidden = true;
    if (!consultations.length) {
      grid.innerHTML = '';
      if (empty) empty.hidden = false;
      return;
    }

    if (empty) empty.hidden = true;
    const t = config.i18n || {};
    const storeLabels = config.storeLabels || {};

    grid.innerHTML = consultations.map(function renderCard(item) {
      const id = String(item.assessment_id || item.assessmentId || '').trim();
      const url = String(item.result_url || item.resultUrl || '').trim();
      const headline = String(item.assessment_headline || item.assessmentHeadline || t.untitled || '').trim();
      const priority = String(item.care_priority || item.carePriority || '').trim();
      const language = String(item.language || '').trim();
      const storeLabel = getStoreLabel(item.store_code || item.storeCode, storeLabels);
      const showLanguage = shouldShowLanguageBadge(language, config.locale);

      return `
        <article class="tara-my-consultations__card" data-consultation-card data-assessment-id="${escapeHtml(id)}">
          <div class="tara-my-consultations__meta">
            <time datetime="${escapeHtml(item.created_at || '')}">${escapeHtml(formatDate(item.created_at, config.locale))}</time>
            ${showLanguage ? `<span class="tara-my-consultations__badge">${escapeHtml(language.toUpperCase())}</span>` : ''}
            ${storeLabel ? `<span class="tara-my-consultations__badge">${escapeHtml(storeLabel)}</span>` : ''}
          </div>
          <h2 class="tara-my-consultations__headline">${escapeHtml(headline)}</h2>
          ${priority ? `<p class="tara-my-consultations__priority">${escapeHtml(priority)}</p>` : ''}
          <div class="tara-my-consultations__actions">
            <a class="tara-my-consultations__button" href="${escapeHtml(url)}">${escapeHtml(t.viewResults || 'View results')}</a>
            <button class="tara-my-consultations__copy" type="button" data-copy-consultation-link data-url="${escapeHtml(url)}" aria-label="${escapeHtml(t.copyLink || 'Copy link')}">
              <span>${escapeHtml(t.copyLink || 'Copy link')}</span>
            </button>
          </div>
        </article>
      `;
    }).join('');
  }

  async function fetchConsultations(config) {
    if (!config.customerId || !config.email) return [];
    const api = window.TaraConsultationApi || null;
    if (!api || typeof api.postJson !== 'function') {
      throw new Error('Consultation API helper unavailable');
    }

    const result = await api.postJson({
      baseUrl: config.workerUrl,
      path: '/api/my-consultations',
      body: {
        shopify_customer_id: config.customerId,
        email: config.email,
        store_code: config.storeCode || ''
      },
      fallbackMessage: 'Saved consultations request failed'
    });

    const payload = result.data;
    return normalizeConsultations(payload?.consultations);
  }

  function showToast(section, message) {
    const toast = section.querySelector('[data-my-consultations-toast]');
    if (!toast) return;
    toast.textContent = message || '';
    toast.hidden = !message;
    clearTimeout(toast._hideTimer);
    toast._hideTimer = setTimeout(function hideToast() {
      toast.hidden = true;
    }, 1800);
  }

  function bindCopy(section, config) {
    section.addEventListener('click', async function onCopyClick(event) {
      const target = event.target instanceof Element ? event.target : null;
      const button = target?.closest('[data-copy-consultation-link]');
      if (!button) return;
      const url = button.getAttribute('data-url') || '';
      if (!url) return;

      try {
        await navigator.clipboard.writeText(url);
        showToast(section, config.i18n?.copied || 'Link copied');
      } catch (error) {
        showToast(section, config.i18n?.copyFailed || 'Could not copy link');
      }
    });
  }

  async function initSection(section) {
    const configEl = section.querySelector('[data-my-consultations-config]');
    if (!configEl) return;

    let config = {};
    try {
      config = JSON.parse(configEl.textContent || '{}');
    } catch (error) {
      config = {};
    }
    config.i18n = decodeStringMap(config.i18n);
    config.storeLabels = decodeStringMap(config.storeLabels);

    const initial = normalizeConsultations(config.consultations);
    bindCopy(section, config);

    if (initial.length) {
      renderCards(section, initial, config);
      return;
    }

    try {
      const remote = await fetchConsultations(config);
      renderCards(section, mergeConsultations(initial, remote), config);
    } catch (error) {
      const loading = section.querySelector('[data-my-consultations-loading]');
      if (loading) loading.hidden = true;
      renderCards(section, [], config);
      const message = window.TaraConsultationApi && typeof window.TaraConsultationApi.friendlyError === 'function'
        ? window.TaraConsultationApi.friendlyError(error, config.i18n || {})
        : (config.i18n?.loadFailed || 'Could not load saved consultations.');
      showToast(section, message);
    }
  }

  function init() {
    document.querySelectorAll('[data-my-consultations-section]').forEach(initSection);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }
})();
